import { Canvas, useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

/**
 * Atmospheric shader overlay: flowing light rays + floating dust particles +
 * soft moss-colored glow blobs. Designed to be layered over video with
 * additive blending so it enriches rather than covers.
 *
 * Uniforms react to `stepFloat` — hue/intensity shift through the 3 steps.
 */

const vertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uStep;
  uniform vec2 uRes;

  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p){
    vec2 i = floor(p); vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }
  float fbm(vec2 p){
    float v = 0.0; float a = 0.5;
    for(int i = 0; i < 5; i++){
      v += a * noise(p);
      p = p * 2.03 + vec2(0.17, 0.43);
      a *= 0.5;
    }
    return v;
  }

  // God-ray style directional streak
  float lightStreaks(vec2 uv, float t){
    // angled coordinates for diagonal rays
    vec2 p = uv;
    p -= 0.5;
    float a = -0.55; // angle
    mat2 R = mat2(cos(a), -sin(a), sin(a), cos(a));
    p = R * p;
    float rays = 0.0;
    rays += smoothstep(0.15, 0.0, abs(sin(p.x * 18.0 + t * 0.3) * 0.1 + p.y * 0.2));
    rays *= smoothstep(0.9, 0.2, length(p));
    return rays;
  }

  // Floating dust specks
  float dust(vec2 uv, float t){
    vec2 p = uv * vec2(uRes.x / uRes.y, 1.0) * 60.0;
    p += vec2(sin(t * 0.1) * 2.0, t * 0.8);
    vec2 i = floor(p);
    vec2 f = fract(p);
    float seed = hash(i);
    if(seed < 0.985) return 0.0;
    vec2 c = f - 0.5;
    float d = 1.0 - smoothstep(0.0, 0.25, length(c));
    float tw = 0.5 + 0.5 * sin(t * 3.0 + seed * 40.0);
    return d * tw;
  }

  void main(){
    vec2 uv = vUv;

    // Flowing soft noise blob — slow drift
    vec2 q = uv * 1.6 + vec2(uTime * 0.02, -uTime * 0.015);
    float n = fbm(q + fbm(q * 1.8 + uTime * 0.04));
    float blob = smoothstep(0.35, 0.75, n);

    // Ray layer
    float rays = lightStreaks(uv, uTime);

    // Dust layer
    float d = dust(uv, uTime);

    // Palette by step: cool moss → warm sage → bright cream glow
    float s = clamp(uStep / 2.0, 0.0, 1.0);
    vec3 cool  = vec3(0.18, 0.36, 0.22);
    vec3 mid   = vec3(0.55, 0.70, 0.45);
    vec3 warm  = vec3(0.95, 0.95, 0.80);
    vec3 tint  = mix(cool, mid, smoothstep(0.0, 0.5, s));
    tint       = mix(tint, warm, smoothstep(0.5, 1.0, s));

    // Compose additive glow
    vec3 col = vec3(0.0);
    col += tint * blob * 0.28;
    col += vec3(0.85, 0.92, 0.75) * rays * 0.22;
    col += vec3(1.0) * d * 0.9;

    // Soft vignette (darker edges → center glow)
    float v = smoothstep(1.05, 0.3, length(uv - 0.5));
    col *= mix(0.6, 1.15, v);

    // Output with alpha driven by brightness — transparent where dark,
    // so layered video shows through naturally.
    float alpha = clamp(col.r + col.g + col.b, 0.0, 1.0) * 0.9;
    gl_FragColor = vec4(col, alpha);
  }
`;

function FogPlane({ stepFloat }: { stepFloat: number }) {
  const mat = useRef<THREE.ShaderMaterial>(null!);
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uStep: { value: 0 },
      uRes: { value: new THREE.Vector2(1, 1) },
    }),
    []
  );
  useFrame((state) => {
    uniforms.uTime.value = state.clock.elapsedTime;
    uniforms.uStep.value += (stepFloat - uniforms.uStep.value) * 0.08;
    uniforms.uRes.value.set(state.size.width, state.size.height);
  });
  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={mat}
        uniforms={uniforms}
        vertexShader={vertex}
        fragmentShader={fragment}
        transparent
        depthTest={false}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

export function PlanShaderBg({ stepFloat }: { stepFloat: number }) {
  return (
    <Canvas
      className="!absolute inset-0"
      orthographic
      camera={{ position: [0, 0, 1], zoom: 1 }}
      dpr={[1, 2]}
      gl={{ antialias: false, alpha: true, premultipliedAlpha: false }}
    >
      <FogPlane stepFloat={stepFloat} />
    </Canvas>
  );
}
